/** Parse a YouTube video / live ID from common URL shapes or a bare ID. */
export function parseYoutubeVideoId(input: string | null | undefined): string | null {
  if (!input) return null;
  const raw = input.trim();
  if (!raw) return null;
  if (/^[\w-]{11}$/.test(raw)) return raw;

  try {
    const url = new URL(raw);
    const host = url.hostname.replace(/^www\./, '');
    if (host === 'youtu.be') {
      const id = url.pathname.split('/').filter(Boolean)[0];
      return id && /^[\w-]{11}$/.test(id) ? id : null;
    }
    if (host === 'youtube.com' || host === 'm.youtube.com' || host === 'music.youtube.com') {
      const v = url.searchParams.get('v');
      if (v && /^[\w-]{11}$/.test(v)) return v;
      const parts = url.pathname.split('/').filter(Boolean);
      const markers = new Set(['live', 'embed', 'shorts', 'v', 'watch']);
      for (let i = 0; i < parts.length; i++) {
        if (markers.has(parts[i]) && parts[i + 1] && /^[\w-]{11}$/.test(parts[i + 1])) {
          return parts[i + 1];
        }
      }
    }
  } catch {
    // not a URL
  }

  const m =
    raw.match(/youtu\.be\/([\w-]{11})/i) ||
    raw.match(/[?&]v=([\w-]{11})/i) ||
    raw.match(/\/(?:live|embed|shorts)\/([\w-]{11})/i);
  return m?.[1] || null;
}

export function youtubeEmbedSrc(
  videoId: string,
  opts: { autoplay?: boolean; mute?: boolean; controls?: boolean } = {}
): string {
  const autoplay = opts.autoplay !== false;
  const mute = opts.mute !== false;
  const controls = opts.controls === true;
  const params = new URLSearchParams({
    playsinline: '1',
    rel: '0',
    modestbranding: '1',
    enablejsapi: '1',
    autoplay: autoplay ? '1' : '0',
    mute: mute ? '1' : '0',
    controls: controls ? '1' : '0',
    fs: '0',
  });
  if (typeof window !== 'undefined' && window.location?.origin) {
    params.set('origin', window.location.origin);
  }
  return `https://www.youtube.com/embed/${encodeURIComponent(videoId)}?${params.toString()}`;
}

/** Resolve YouTube id from session fields (URL preferred, then stored id). */
export function sessionYoutubeId(session: {
  facebook_live_url?: string | null;
  facebook_video_id?: string | null;
}): string | null {
  return (
    parseYoutubeVideoId(session.facebook_live_url) ||
    parseYoutubeVideoId(session.facebook_video_id)
  );
}

export function sessionYoutubeUrl(session: {
  facebook_live_url?: string | null;
  facebook_video_id?: string | null;
}): string | null {
  if (session.facebook_live_url && parseYoutubeVideoId(session.facebook_live_url)) {
    return session.facebook_live_url;
  }
  const id = sessionYoutubeId(session);
  return id ? `https://www.youtube.com/watch?v=${id}` : null;
}
